/**
 * Semantic Design Tokens
 *
 * Component-specific design tokens extracted from Figma Variables.
 * These tokens provide semantic meaning and map to primitive color values.
 *
 * Token naming convention: Category/property-variant-state
 * Example: Button/fill-primary-hover, Icon/secondary, Text/primary
 */

import { lightModeShades } from '../colors/light-mode-shades';

// ============================================================================
// Button Tokens
// ============================================================================

const buttonTokens = {
  fill: {
    primary: {
      enabled: lightModeShades.blue[600], // #364B5E
      hover: lightModeShades.blue[700], // #1C3042
      pressed: lightModeShades.blue[800], // #001F28
    },
    secondary: {
      enabled: lightModeShades.navy[100], // #B9C4D1
      hover: lightModeShades.navy[200], // #8C9FB2
      focused: lightModeShades.navy[200], // #8C9FB2
      pressed: lightModeShades.navy[300], // #627A94
    },
    error: {
      enabled: lightModeShades.red[400], // #CD0E04
      hover: lightModeShades.red[500], // #93000A
      focused: lightModeShades.red[500], // #93000A
      pressed: lightModeShades.red[600], // #690005
    },
    text: {
      enabled: 'transparent',
      hover: 'rgba(0, 0, 0, 0.04)',
      focused: 'rgba(0, 0, 0, 0.08)',
      pressed: 'rgba(0, 0, 0, 0.12)',
    },
    disabled: lightModeShades.grey[250], // #DCE4E8
    fab: lightModeShades.blue[400], // #1994B3
  },
  border: {
    secondary: {
      enabled: lightModeShades.navy[200], // #8C9FB2
    },
    disabled: lightModeShades.grey[250], // #DCE4E8
  },
} as const;

// ============================================================================
// Icon Tokens
// ============================================================================

const iconTokens = {
  primary: lightModeShades.navy[900], // #021E38
  secondary: lightModeShades.navy[400], // #405F80
  tertiary: lightModeShades.navy[200], // #8C9FB2
  disabled: lightModeShades.grey[300], // #A9ABAD
  inverse: lightModeShades.grey[100], // #FFFFFF
  error: lightModeShades.red[400], // #CD0E04
  warning: lightModeShades.yellow[400], // #DC7F3B
  success: lightModeShades.green[400], // #26AB76
  alert: {
    info: {
      primary: lightModeShades.navy[900], // #021E38
      secondary: lightModeShades.navy[100], // #B9C4D1
    },
  },
  iconButton: {
    bg: {
      hover: 'rgba(0, 0, 0, 0.04)',
      focused: 'rgba(0, 0, 0, 0.08)',
      pressed: 'rgba(0, 0, 0, 0.12)',
      disabled: lightModeShades.grey[250], // #DCE4E8
      tonal: {
        hover: lightModeShades.navy[75], // #D2DAE3
        focused: lightModeShades.navy[100], // #B9C4D1
        pressed: lightModeShades.navy[200], // #8C9FB2
      },
    },
  },
  bg: {
    white: lightModeShades.grey[100], // #FFFFFF
    accentTeal: lightModeShades.teal[50], // #E0F3F1
  },
} as const;

// ============================================================================
// Text Tokens
// ============================================================================

const textTokens = {
  primary: lightModeShades.navy[900], // #021E38
  secondary: lightModeShades.navy[400], // #405F80
  tertiary: lightModeShades.navy[200], // #8C9FB2
  disabled: lightModeShades.grey[300], // #A9ABAD
  inverse: lightModeShades.grey[100], // #FFFFFF
  accent: lightModeShades.blue[400], // #1994B3
  link: lightModeShades.blue[400], // #1994B3
  error: lightModeShades.red[400], // #CD0E04
  warning: lightModeShades.yellow[400], // #DC7F3B
  success: lightModeShades.green[400], // #26AB76
  onError: lightModeShades.grey[100], // #FFFFFF
  onWarning: lightModeShades.grey[100], // #FFFFFF
  onSuccess: lightModeShades.grey[100], // #FFFFFF
  tag: lightModeShades.navy[900], // #021E38
  button: {
    disabled: lightModeShades.grey[300], // #A9ABAD
  },
} as const;

// ============================================================================
// Surface Tokens
// ============================================================================

const surfaceTokens = {
  page: {
    bg: {
      primary: lightModeShades.grey[200], // #FBFCFE
    },
  },
  card: {
    bg: {
      primary: lightModeShades.grey[100], // #FFFFFF
    },
  },
  container: {
    bg: {
      primary: lightModeShades.grey[100], // #FFFFFF
    },
  },
  drawer: {
    bg: {
      primary: lightModeShades.grey[100], // #FFFFFF
    },
  },
  header: {
    bg: {
      primary: lightModeShades.navy[900], // #021E38
      enabled: lightModeShades.navy[900], // #021E38
    },
  },
  sidebar: {
    bg: {
      primary: lightModeShades.grey[100], // #FFFFFF
    },
  },
  sidebarItem: {
    bg: {
      active: lightModeShades.navy[50], // #E3E8EC
    },
  },
  accordion: {
    header: {
      hover: lightModeShades.grey[225], // #EFF1F2
      focused: lightModeShades.grey[250], // #DCE4E8
    },
  },
  infoBox: {
    bg: {
      neutral: lightModeShades.cream[25], // #FCF7F0
    },
  },
  sigma: {
    bg: {
      primary: lightModeShades.grey[225], // #EFF1F2
    },
  },
  accent: {
    blue: lightModeShades.accent.blue, // #C9D9EE
    purple: lightModeShades.accent.purple, // #E6E2F2
    coral: lightModeShades.accent.coral, // #FFA290
    teal: lightModeShades.teal[50], // #E0F3F1
    neutral: lightModeShades.navy[50], // #E3E8EC
    neutralDark: lightModeShades.navy[100], // #B9C4D1
  },
} as const;

// ============================================================================
// Border Tokens
// ============================================================================

const borderTokens = {
  primary: lightModeShades.navy[75], // #D2DAE3
  secondary: lightModeShades.navy[200], // #8C9FB2
  error: lightModeShades.red[400], // #CD0E04
  warning: lightModeShades.yellow[400], // #DC7F3B
  success: lightModeShades.green[400], // #26AB76
  chip: {
    secondary: lightModeShades.navy[200], // #8C9FB2
  },
  button: {
    disabled: lightModeShades.grey[250], // #DCE4E8
  },
  radius: {
    sm: '8px',
    md: '12px',
    lg: '16px',
    full: '100px',
  },
} as const;

// ============================================================================
// Alert Tokens
// ============================================================================

const alertTokens = {
  bg: {
    error: {
      primary: lightModeShades.red[400], // #CD0E04
      secondary: lightModeShades.red[200], // #FFC7BB
    },
    warning: {
      primary: lightModeShades.yellow[400], // #DC7F3B
      secondary: lightModeShades.yellow[200], // #F9DDB5
    },
    success: {
      primary: lightModeShades.green[400], // #26AB76
      secondary: lightModeShades.green[100], // #D2EFE7
    },
    neutralDark: {
      primary: lightModeShades.navy[900], // #021E38
      secondary: lightModeShades.navy[100], // #B9C4D1
    },
    neutralLight: {
      primary: lightModeShades.navy[600], // #0E4067
      secondary: lightModeShades.navy[50], // #E3E8EC
    },
    white: lightModeShades.grey[100], // #FFFFFF
  },
} as const;

// ============================================================================
// Chip Tokens
// ============================================================================

const chipTokens = {
  bg: {
    primary: {
      active: lightModeShades.navy[100], // #B9C4D1
      inactive: lightModeShades.grey[225], // #EFF1F2
      selected: lightModeShades.blue[400], // #1994B3
    },
    filled: {
      primary: {
        enabled: lightModeShades.blue[400], // #1994B3
      },
    },
    outline: {
      primary: {
        enabled: 'transparent',
      },
      selected: lightModeShades.navy[50], // #E3E8EC
    },
    focused: lightModeShades.navy[75], // #D2DAE3
  },
} as const;

// ============================================================================
// Data Table Tokens
// ============================================================================

const dataTableTokens = {
  bg: {
    row: {
      active: lightModeShades.navy[75], // #D2DAE3
      hover: lightModeShades.grey[225], // #EFF1F2
    },
  },
} as const;

// ============================================================================
// Semantic Tokens - All Combined
// ============================================================================

export const semantic = {
  button: buttonTokens,
  icon: iconTokens,
  text: textTokens,
  surface: surfaceTokens,
  border: borderTokens,
  alert: alertTokens,
  chip: chipTokens,
  dataTable: dataTableTokens,
} as const;
// Type exports for TypeScript
export type ButtonTokens = typeof buttonTokens;
export type IconTokens = typeof iconTokens;
export type TextTokens = typeof textTokens;
export type SurfaceTokens = typeof surfaceTokens;
export type BorderTokens = typeof borderTokens;
export type AlertTokens = typeof alertTokens;
export type ChipTokens = typeof chipTokens;
export type DataTableTokens = typeof dataTableTokens;
export type SemanticTokens = typeof semantic;
